import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import urlJoin from 'url-join';
import camelize from 'camelize';
import { connect } from 'react-redux';
import { urls } from 'app-constants';
import { simpleMinify } from 'utils';
import LoadingSpinner from 'components/common/LoadingSpinner';
import Icon from 'components/common/Icon';


const TYPE_RESPONSIVE = 'responsive';
const TYPE_LEGACY = 'legacy';
const TYPE_FIXED = 'fixed';

const ReleaseEmbedCode = ({
  releaseId,
  lastPublishTimestamp,
  includeDirectLink = true,
  hasInternalDomains = false,
  navOffset = 30,
}) => {
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [publishHistory, setPublishHistory] = useState([]);
  const [error, setError] = useState(null);
  const [embedType, setEmbedType] = useState(TYPE_RESPONSIVE);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [optimizeForInternal, setOptimizeForInternal] = useState(false);

  const [size, setSize] = useState({ width: 800, height: 610 });
  const updateSize = attrs => setSize(os => ({ ...os, ...attrs }));

  useEffect(() => {
    fetchPublishHistory();
  }, []);

  const copyTimeout = useRef();
  const linkCopyTimeout = useRef();
  const textareaRef = useRef();
  const linkInputRef = useRef();

  useEffect(() => {
    if (copied) {
      clearTimeout(copyTimeout.current);
      copyTimeout.current = setTimeout(() => setCopied(false), 1500);
    }
    return () => clearTimeout(copyTimeout.current);
  }, [copied]);

  useEffect(() => {
    if (linkCopied) {
      clearTimeout(linkCopyTimeout.current);
      linkCopyTimeout.current = setTimeout(() => setLinkCopied(false), 1500);
    }
    return () => clearTimeout(linkCopyTimeout.current);
  }, [linkCopied]);

  useEffect(() => {
    setHistoryLoaded(false);
    fetchPublishHistory();
  }, [lastPublishTimestamp]);

  const handleCopyCodeClick = evt => {
    evt.preventDefault();
    textareaRef.current.select();
    document.execCommand('copy');
    setCopied(true);
  };

  const handleCopyLinkClick = evt => {
    evt.preventDefault();
    linkInputRef.current.select();
    document.execCommand('copy');
    setLinkCopied(true);
  };

  const handleTextareaClick = evt => evt.target.select();

  const handleEmbedTypeChange = evt => setEmbedType(evt.target.value);

  const handleSizeChange = evt => {
    const { name, value } = evt.target;
    updateSize({ [name]: value });
  };

  const handleOptimizeChange = evt => {
    setOptimizeForInternal(evt.target.checked);
  };

  const fetchPublishHistory = () => {
    const url = urlJoin(urls.releasePublishHistoryBase, releaseId, '/');
    fetch(url, { credentials: 'include' })
      .then(response => {
        if (!response.ok) {
          setError(response.statusText);
        }
        return response;
      })
      .then(response => response.json())
      .then(data => camelize(data))
      .then(({ publishHistory }) => {
        setPublishHistory(publishHistory.filter(({ result }) => result === 'completed'));
        setHistoryLoaded(true);
      })
      .catch(err => {
        setError(err);
        console.error(err);
      });
  };

  const renderEmbedCode = url => {
    const iframeResponsive = `<iframe src="${url}" style="position:absolute;top:0;left:0;width:100%;height:100%;" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;

    const baseOffset = optimizeForInternal ? 45 : 130;
    const offset = baseOffset + navOffset;

    switch (embedType) {
      case TYPE_RESPONSIVE:
        return simpleMinify(`
          <style>
            .ms-embed-${releaseId} {
              padding-bottom: 75%;
              padding-bottom:calc(56.25% + ${offset}px);
            }
            @media (max-width: 480px) {
              .ms-embed-${releaseId} {
                padding-bottom:calc(100vh - 100px);
              }
            }
          </style>
          <div class="ms-embed-${releaseId}" style="position:relative">${iframeResponsive}</div>
        `);
      case TYPE_LEGACY:
        return `<div style="padding-bottom:75%;padding-bottom:calc(56.25% + ${offset}px);position:relative;">${iframeResponsive}</div>`;
      case TYPE_FIXED:
        return `<iframe src="${url}" width="${size.width}" height="${size.height}" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
    }
  };

  const renderCopiedOverlay = copied => (
    <div className="release-embed-code-copied" style={{ opacity: copied ? 1 : 0 }}>
      <span className="identifier">Copied!</span>
    </div>
  );

  const getErrors = () => {
    const renderError = (msg, style = 'error') => (
      <div className={`${style}-list mb-4`}>
        <ul className="m-0">
          <li>{msg}</li>
        </ul>
      </div>
    );

    if (historyLoaded && !publishHistory.length) {
      return renderError('This release has not yet been published.', 'warning');
    }

    if (error) {
      return renderError('Error retrieving Release details.');
    }

    if (historyLoaded) {
      const [lastPublishResult] = publishHistory.slice().reverse();
      const { url } = lastPublishResult;

      if (!url) {
        return renderError('No URL specified for this S3 bucket. Check that your S3 integration is configured correctly.');
      }
    }

    return null;
  };

  let contents;
  let linkField;
  if (!historyLoaded) {
    contents = (
      <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner />
      </div>
    );
  } else {
    const [lastPublishResult] = publishHistory.slice().reverse();
    const { url } = lastPublishResult;

    contents = (
      <div className="mb-2" style={{ position: 'relative' }}>
        <textarea
          ref={textareaRef}
          cols="30"
          rows="8"
          className="form-input release-embed-code-textarea"
          value={renderEmbedCode(url)}
          onClick={handleTextareaClick}
          readOnly
        />
        {renderCopiedOverlay(copied)}
      </div>
    );

    linkField = (
      <div className="mb-2">
        <div style={{ position: 'relative' }}>
          <div className="input-group">
            <input
              ref={linkInputRef}
              type="text"
              className="form-input release-embed-code-link-input"
              value={url}
              readOnly
            />
            <span className="input-group-addon">
              <a href={url} target="_blank" rel="noopener noreferrer" className="d-flex td-none">
                <Icon name="open_in_new" />
              </a>
            </span>
          </div>
          {renderCopiedOverlay(linkCopied)}
        </div>
        <p className="form-input-hint">
          View the published player. You can also share this URL on Twitter to embed the player in a Tweet.
        </p>
      </div>
    );
  }

  const options = (
    <div>
      <label className="form-radio m-0">
        <input
          type="radio"
          value={TYPE_RESPONSIVE}
          checked={embedType === TYPE_RESPONSIVE}
          onChange={handleEmbedTypeChange}
        />
        <i className="form-icon" /> <strong>Responsive</strong>
        <div className="text-hint">Embed will scale to fit its container.</div>
      </label>
      {hasInternalDomains && (
        <label className="form-checkbox" style={{ margin: '0 0 0 1.2rem' }}>
          <input
            type="checkbox"
            name="skipOverlay"
            checked={optimizeForInternal}
            onChange={handleOptimizeChange}
          />
          <i className="form-icon" /> <span className="text-sm">Optimize for internal domains</span>
        </label>
      )}
      <label className="form-radio m-0">
        <input
          type="radio"
          value={TYPE_LEGACY}
          checked={embedType === TYPE_LEGACY}
          onChange={handleEmbedTypeChange}
        />
        <i className="form-icon" /> <strong>Responsive (legacy)</strong>
        <div className="text-hint">Use this option if you experience problems using the standard responsive code in your publishing system.</div>
      </label>
      <label className="form-radio m-0">
        <input
          type="radio"
          value={TYPE_FIXED}
          checked={embedType === TYPE_FIXED}
          onChange={handleEmbedTypeChange}
        />
        <i className="form-icon" /> <strong>Fixed size</strong>
        <div className="text-hint">Embed will display at the exact width and height specified.</div>
      </label>

      {embedType === TYPE_FIXED && (
        <div className="mt-1" style={{ display: 'flex', paddingLeft: '1.2rem' }}>
          <input name="width" type="text" className="form-input input-sm" style={{ width: 40 }} value={size.width} onChange={handleSizeChange} />
          <span className="mx-1">x</span>
          <input name="height" type="text" className="form-input input-sm" style={{ width: 40 }} value={size.height} onChange={handleSizeChange} />
          <span className="mx-1">pixels</span>
        </div>
      )}
    </div>
  );

  const showExtras = historyLoaded && !error && publishHistory.length > 0 && !!publishHistory[publishHistory.length - 1].url;

  const errors = getErrors();

  return (
    <>
      <div className="form-group mb-4">
        <div className="mb-2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <h5 className="mb-0">Embed code</h5>
          {showExtras && <a className="text-sm text-upper" href="#copy" onClick={handleCopyCodeClick}><strong>copy to clipboard</strong></a>}
        </div>
        {errors || contents}
        {showExtras && options}
      </div>

      {!errors && showExtras && includeDirectLink && (
        <div className="form-group mb-4">
          <div className="mb-2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <h5 className="mb-0">Direct link</h5>
            <a className="text-sm text-upper" href="#copy" onClick={handleCopyLinkClick}><strong>copy to clipboard</strong></a>
          </div>
          {linkField}
        </div>
      )}
    </>
  );
};

ReleaseEmbedCode.propTypes = {
  releaseId: PropTypes.string,
  lastPublishTimestamp: PropTypes.number,
  includeDirectLink: PropTypes.bool,
  hasInternalDomains: PropTypes.bool,
  navOffset: PropTypes.number,
};

const mapStateToProps = ({ releaseDetail }) => ({
  lastPublishTimestamp: releaseDetail.timestamp,
});

export default connect(mapStateToProps)(ReleaseEmbedCode);
